import { Dialog, Listbox, Transition } from "@headlessui/react";
import {
    CheckIcon,
    ChevronUpDownIcon,
    XMarkIcon,
} from "@heroicons/react/24/solid";
import { Fragment, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LoadingOverlay from "../LoadingOverlay";
import DepartmentBadge from "../deparments/DepartmentBadge";
import { ensureOneTeamPerDepartment } from "../../helpers/clientTeamRule";
import { getTeams } from "../../services/teams/getTeams";
import { updateClient } from "../../services/updateClient";

const NO_DEPARTMENT_ID = "__no_department__";

const getDepartmentId = (team) =>
    team?.departmentId ??
    team?.deparmentId ??
    team?.department?.id ??
    team?.department?.departmentId ??
    NO_DEPARTMENT_ID;

const getDepartmentName = (team) => {
    if (typeof team?.department === "string") return team.department;

    return (
        team?.departmentName ??
        team?.deparmentName ??
        team?.department?.name ??
        team?.department?.departmentName ??
        (getDepartmentId(team) === NO_DEPARTMENT_ID
            ? "No department"
            : "Unknown department")
    );
};

const getDepartmentColorHex = (team) =>
    team?.colorHex ?? team?.department?.colorHex ?? team?.departmentColorHex ?? null;

const getTeamKey = (team) => {
    if (!team) return "";

    const departmentId = getDepartmentId(team);
    const teamName = team?.name || team?.teamName || "";

    return team?.id || team?.teamId || `${departmentId}-${teamName}`;
};

const dedupeTeamsById = (teams) => {
    const map = new Map();

    (teams || []).forEach((team) => {
        const key = getTeamKey(team);
        if (!key || map.has(key)) return;

        map.set(key, team);
    });

    return Array.from(map.values());
};

const getTeamMembers = (team) => {
    const members =
        team?.members ??
        team?.teamMembers ??
        team?.employees ??
        team?.teamEmployees ??
        [];

    return Array.isArray(members) ? members : [];
};

const getMemberName = (member) => {
    if (!member) return "";
    if (typeof member === "string") return member;

    const fullName = member.fullName ?? member.name ?? member.employeeName ?? "";
    if (fullName) return fullName;

    return `${member.firstName ?? ""} ${member.lastName ?? ""}`.trim();
};

const teamMatchesSearch = (team, search) => {
    if (!search) return true;

    const needle = search.toLowerCase();
    const memberNames = getTeamMembers(team).map(getMemberName).join(" ");

    return [team?.name, getDepartmentName(team), memberNames]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle));
};

const buildSelectionsByDepartment = (teams) => {
    const selected = {};
    let hadDuplicates = false;

    (teams || []).forEach((team) => {
        const departmentId = getDepartmentId(team);
        if (selected[departmentId]) {
            hadDuplicates = true;
            return;
        }

        selected[departmentId] = team;
    });

    return { selected, hadDuplicates };
};

export default function UpdateClientTeamsModal({
    open,
    setOpen,
    mode = "edit",
    client,
    clientName,
    selectedTeams = null,
    onApply,
    onSaved,
}) {
    const [teams, setTeams] = useState([]);
    const [selectedTeamsByDepartment, setSelectedTeamsByDepartment] = useState({});
    const [searchText, setSearchText] = useState("");
    const [appliedSearch, setAppliedSearch] = useState("");
    const [loadingTeams, setLoadingTeams] = useState(false);
    const [saving, setSaving] = useState(false);
    const [duplicateWarning, setDuplicateWarning] = useState(false);
    const [validationMessage, setValidationMessage] = useState("");
    const [expandedDepartments, setExpandedDepartments] = useState({});

    useEffect(() => {
        if (!open) return;

        const initialTeams = mode === "create" ? selectedTeams : client?.teams ?? [];
        const { selected, hadDuplicates } = buildSelectionsByDepartment(
            initialTeams,
        );
        const nextExpandedDepartments = {};

        Object.keys(selected).forEach((departmentId) => {
            if (selected[departmentId]) {
                nextExpandedDepartments[departmentId] = true;
            }
        });

        setSelectedTeamsByDepartment(selected);
        setExpandedDepartments(nextExpandedDepartments);
        setSearchText("");
        setAppliedSearch("");
        setDuplicateWarning(hadDuplicates);
        setValidationMessage("");

        const loadTeams = async () => {
            setLoadingTeams(true);
            const response = await getTeams();

            if (response?.success && response?.data?.success) {
                setTeams(response.data.teams || []);
            } else {
                setTeams([]);
                toast.error(
                    response?.data?.message ||
                        response?.message ||
                        "Teams could not be loaded.",
                );
            }

            setLoadingTeams(false);
        };

        loadTeams();
    }, [open, mode, client, selectedTeams]);

    const departments = useMemo(() => {
        const map = new Map();
        const initialTeams = mode === "create" ? selectedTeams : client?.teams ?? [];
        const allTeams = dedupeTeamsById([
            ...(initialTeams || []),
            ...(teams || []),
        ]);

        allTeams.forEach((team) => {
            const departmentId = getDepartmentId(team);
            const departmentName = getDepartmentName(team);
            const colorHex = getDepartmentColorHex(team);

            if (!map.has(departmentId)) {
                map.set(departmentId, {
                    id: departmentId,
                    name: departmentName,
                    colorHex,
                    teams: [],
                });
            }

            const group = map.get(departmentId);
            if (!group.colorHex && colorHex) group.colorHex = colorHex;

            if (teamMatchesSearch(team, appliedSearch)) {
                group.teams.push(team);
            }
        });

        return Array.from(map.values())
            .map((department) => ({
                ...department,
                teams: department.teams.sort((a, b) =>
                    (a?.name || "").localeCompare(b?.name || ""),
                ),
            }))
            .filter((department) => !appliedSearch || department.teams.length > 0)
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [teams, mode, client?.teams, selectedTeams, appliedSearch]);

    const selectedTeamsArray =
        Object.values(selectedTeamsByDepartment).filter(Boolean);

    const isDepartmentExpanded = (departmentId) => {
        return !!expandedDepartments[departmentId];
    };

    const expandDepartment = (departmentId) => {
        setExpandedDepartments((prev) => ({
            ...prev,
            [departmentId]: true,
        }));
    };

    const collapseDepartment = (departmentId) => {
        setExpandedDepartments((prev) => ({
            ...prev,
            [departmentId]: false,
        }));
    };

    const handleSearchKeyDown = (event) => {
        if (event.key !== "Enter") return;
        event.preventDefault();
        setAppliedSearch(searchText.trim());
    };

    const handleSelectTeam = (departmentId, team) => {
        setValidationMessage("");
        expandDepartment(departmentId);
        setSelectedTeamsByDepartment((prev) => ({
            ...prev,
            [departmentId]: team,
        }));
    };

    const handleClearTeam = (departmentId) => {
        setValidationMessage("");
        setSelectedTeamsByDepartment((prev) => {
            const next = { ...prev };
            delete next[departmentId];
            return next;
        });
        collapseDepartment(departmentId);
    };

    const handleSave = async () => {
        setValidationMessage("");

        const selectedTeamIds = Object.values(selectedTeamsByDepartment)
            .filter(Boolean)
            .map((team) => team.id || team.teamId)
            .filter(Boolean);

        if (selectedTeamIds.length === 0) {
            const message = "Please select at least one team before saving.";
            setValidationMessage(message);
            toast.error(message);
            return;
        }

        const teamRule = ensureOneTeamPerDepartment(selectedTeamsArray);
        if (!teamRule.ok) {
            toast.error(teamRule.message);
            return;
        }

        if (mode === "create") {
            if (onApply) onApply(selectedTeamsArray);
            setValidationMessage("");
            setOpen(false);
            return;
        }

        setSaving(true);
        const response = await updateClient({
            ...client,
            clientDetail: { ...(client?.clientDetail ?? {}) },
            address: { ...(client?.address ?? {}) },
            teams: selectedTeamsArray,
        });
        setSaving(false);

        if (response?.success) {
            toast.success(response.message || "Client teams updated.");
            if (onSaved) await onSaved();
            setValidationMessage("");
            setOpen(false);
        } else {
            toast.error(response?.message || "Client teams could not be updated.");
        }
    };

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-[100]" onClose={setOpen}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-slate-900/40" />
                </Transition.Child>

                <div className="fixed inset-0 z-[110] bg-white">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0 translate-y-4"
                        enterTo="opacity-100 translate-y-0"
                        leave="ease-in duration-150"
                        leaveFrom="opacity-100 translate-y-0"
                        leaveTo="opacity-0 translate-y-4"
                    >
                        <Dialog.Panel className="flex h-screen w-screen flex-col bg-white">
                            <header className="shrink-0 border-b border-slate-200 bg-white px-4 py-4 sm:px-6 lg:px-8">
                                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                                    <div className="min-w-0">
                                        <Dialog.Title className="text-xl font-bold text-slate-900">
                                            Edit client teams
                                        </Dialog.Title>
                                        <p className="mt-1 truncate text-sm text-slate-500">
                                            {mode === "create"
                                                ? clientName
                                                : client?.clientName}
                                        </p>
                                    </div>

                                    <div className="flex flex-1 flex-col gap-2 lg:max-w-xl">
                                        <div className="flex gap-2">
                                            <input
                                                type="search"
                                                value={searchText}
                                                onChange={(event) =>
                                                    setSearchText(event.target.value)
                                                }
                                                onKeyDown={handleSearchKeyDown}
                                                placeholder="Search teams"
                                                className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm text-slate-700 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-0"
                                            />
                                            {appliedSearch ? (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setSearchText("");
                                                        setAppliedSearch("");
                                                    }}
                                                    className="h-10 shrink-0 rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
                                                >
                                                    Clear
                                                </button>
                                            ) : null}
                                            <button
                                                type="button"
                                                onClick={() => setOpen(false)}
                                                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-500 shadow-sm hover:bg-slate-50"
                                                aria-label="Close"
                                            >
                                                <XMarkIcon className="h-5 w-5" />
                                            </button>
                                        </div>
                                        <p className="text-xs text-slate-500">
                                            Type a team name and press Enter to search.
                                        </p>
                                    </div>
                                </div>
                            </header>

                            <main className="min-h-0 flex-1 overflow-y-auto px-4 py-6 sm:px-6 lg:px-8">
                                {duplicateWarning ? (
                                    <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                                        This client had multiple teams in the same
                                        department. Only one was kept for editing.
                                    </div>
                                ) : null}

                                {loadingTeams ? (
                                    <div className="py-12 text-center text-sm text-slate-500">
                                        Loading teams...
                                    </div>
                                ) : departments.length ? (
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                                        {departments.map((department) => {
                                            const selectedTeam =
                                                selectedTeamsByDepartment[
                                                    department.id
                                                ] ?? null;

                                            return (
                                                <DepartmentColumn
                                                    key={department.id}
                                                    department={department}
                                                    selectedTeam={selectedTeam}
                                                    isExpanded={isDepartmentExpanded(
                                                        department.id,
                                                    )}
                                                    onExpand={() =>
                                                        expandDepartment(
                                                            department.id,
                                                        )
                                                    }
                                                    onCollapse={() =>
                                                        collapseDepartment(
                                                            department.id,
                                                        )
                                                    }
                                                    onSelect={(team) =>
                                                        handleSelectTeam(
                                                            department.id,
                                                            team,
                                                        )
                                                    }
                                                    onClear={() =>
                                                        handleClearTeam(
                                                            department.id,
                                                        )
                                                    }
                                                />
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="rounded-lg border border-dashed border-slate-300 px-4 py-10 text-center text-sm text-slate-500">
                                        No teams match the applied search.
                                    </div>
                                )}
                            </main>

                            <footer className="shrink-0 border-t border-slate-200 bg-white px-4 py-4 sm:px-6 lg:px-8">
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
                                    {validationMessage ? (
                                        <p className="text-sm font-medium text-red-600 sm:mr-auto">
                                            {validationMessage}
                                        </p>
                                    ) : null}
                                    <button
                                        type="button"
                                        onClick={() => setOpen(false)}
                                        className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-0"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSave}
                                        disabled={saving}
                                        className="inline-flex justify-center rounded-md border border-transparent bg-teal-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-teal-600 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        Save teams
                                    </button>
                                </div>
                            </footer>

                            <LoadingOverlay
                                isLoading={saving}
                                message="Saving client teams..."
                            />
                        </Dialog.Panel>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    );
}

function DepartmentColumn({
    department,
    selectedTeam,
    isExpanded,
    onExpand,
    onCollapse,
    onSelect,
    onClear,
}) {
    if (!selectedTeam && !isExpanded) {
        return (
            <section className="flex min-h-[180px] flex-col rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <button
                    type="button"
                    onClick={onExpand}
                    className="flex h-full min-h-[140px] w-full items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm font-semibold text-slate-600 hover:border-teal-400 hover:bg-teal-50 hover:text-teal-700"
                >
                    + Add {department.name} Team
                </button>
            </section>
        );
    }

    return (
        <section className="flex min-h-[260px] flex-col rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-lg font-bold text-slate-800">
                            {department.name}
                        </h3>
                        <DepartmentBadge
                            name={department.name}
                            colorHex={department.colorHex}
                        />
                    </div>
                    <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Current selection
                    </p>
                    <p className="mt-1 truncate text-sm font-medium text-slate-800">
                        {selectedTeam?.name || "No team selected"}
                    </p>
                </div>
                {selectedTeam ? (
                    <button
                        type="button"
                        onClick={onClear}
                        className="inline-flex shrink-0 items-center rounded-md bg-red-50 px-3 py-1.5 text-sm font-semibold text-red-700 ring-1 ring-inset ring-red-200 hover:bg-red-100"
                    >
                        Cancel
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={onCollapse}
                        className="inline-flex shrink-0 items-center rounded-md bg-red-50 px-3 py-1.5 text-sm font-semibold text-red-700 ring-1 ring-inset ring-red-200 hover:bg-red-100"
                    >
                        Cancel
                    </button>
                )}
            </div>

            <Listbox
                by={(a, b) => getTeamKey(a) === getTeamKey(b)}
                value={selectedTeam}
                onChange={onSelect}
            >
                <div className="relative mt-4">
                    <Listbox.Button className="inline-flex w-full justify-between rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-normal text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-0">
                        <span className="truncate">
                            {selectedTeam?.name || "Select a team"}
                        </span>
                        <ChevronUpDownIcon className="h-5 w-5 shrink-0" />
                    </Listbox.Button>

                    <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                    >
                        <Listbox.Options className="absolute z-30 mt-2 max-h-72 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {department.teams.map((team) => (
                                <Listbox.Option
                                    key={getTeamKey(team)}
                                    value={team}
                                    className={({ active }) =>
                                        `${
                                            active
                                                ? "bg-blue-600 text-white"
                                                : "text-gray-900"
                                        } relative cursor-pointer select-none py-2 pl-8 pr-4`
                                    }
                                >
                                    {({ active, selected }) => (
                                        <>
                                            {selected ? (
                                                <span
                                                    className={`${
                                                        active
                                                            ? "text-white"
                                                            : "text-blue-600"
                                                    } absolute inset-y-0 left-0 flex items-center pl-1.5`}
                                                >
                                                    <CheckIcon className="h-5 w-5" />
                                                </span>
                                            ) : null}
                                            <span
                                                className={`block truncate ${
                                                    selected
                                                        ? "font-semibold"
                                                        : "font-normal"
                                                }`}
                                            >
                                                {team.name}
                                            </span>
                                        </>
                                    )}
                                </Listbox.Option>
                            ))}
                        </Listbox.Options>
                    </Transition>
                </div>
            </Listbox>

            <TeamMembersPreview team={selectedTeam} />
        </section>
    );
}

function TeamMembersPreview({ team }) {
    const members = getTeamMembers(team);

    if (!team) {
        return (
            <p className="mt-4 text-xs text-slate-500">
                Select a team to preview members.
            </p>
        );
    }

    if (!members.length) {
        return (
            <p className="mt-4 text-xs text-slate-500">
                No team members assigned.
            </p>
        );
    }

    return (
        <div className="mt-4 border-t border-slate-100 pt-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Team members
            </p>
            <ul className="mt-2 space-y-1">
                {members.slice(0, 5).map((member) => {
                    const name = getMemberName(member) || "Unnamed member";

                    return (
                        <li
                            key={member?.employeeId ?? member?.id ?? name}
                            className="truncate text-sm text-slate-700"
                            title={name}
                        >
                            {name}
                        </li>
                    );
                })}
            </ul>
            {members.length > 5 ? (
                <p className="mt-2 text-xs text-slate-500">
                    +{members.length - 5} more
                </p>
            ) : null}
        </div>
    );
}
